'use client';

import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import { cn } from '@/lib/utils';

function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  );
}

// Context to share hover state between Tooltip and TooltipTrigger
const TooltipHoverContext = React.createContext<{
  setIsHovered: (hovered: boolean) => void;
} | null>(null);

function Tooltip({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  const [open, setOpen] = React.useState(false);
  const [isHovered, setIsHovered] = React.useState(false);

  // Update open state when hover state changes
  React.useEffect(() => {
    if (isHovered) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [isHovered]);

  return (
    <TooltipProvider>
      <TooltipHoverContext.Provider value={{ setIsHovered }}>
        <TooltipPrimitive.Root
          data-slot="tooltip"
          open={open}
          onOpenChange={(newOpen) => {
            // Only allow tooltip to open if it's from hover, not from focus
            // If Radix tries to open it (e.g., on focus), only allow if hovered
            if (newOpen && isHovered) {
              setOpen(true);
            } else if (!newOpen) {
              setOpen(false);
            }
          }}
          {...props}
        />
      </TooltipHoverContext.Provider>
    </TooltipProvider>
  );
}

function TooltipTrigger({
  onFocus,
  onPointerEnter,
  onPointerLeave,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  const context = React.useContext(TooltipHoverContext);

  return (
    <TooltipPrimitive.Trigger
      data-slot="tooltip-trigger"
      onFocus={(e) => {
        // Don't show tooltip on keyboard focus - only on hover
        // Allow focus for accessibility but prevent tooltip from opening
        onFocus?.(e);
      }}
      onPointerEnter={(e) => {
        // Mark as hovered so tooltip can open
        context?.setIsHovered(true);
        onPointerEnter?.(e);
      }}
      onPointerLeave={(e) => {
        // Mark as not hovered
        context?.setIsHovered(false);
        onPointerLeave?.(e);
      }}
      {...props}
    />
  );
}

type TooltipContentProps = React.ComponentProps<
  typeof TooltipPrimitive.Content
> & {
  hideArrow?: boolean;
};

function TooltipContent({
  className,
  hideArrow = false,
  sideOffset = 0,
  children,
  ...props
}: TooltipContentProps) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          'bg-foreground text-background animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-[10000] w-fit origin-(--radix-tooltip-content-transform-origin) rounded-md px-3 py-1.5 text-xs text-balance',
          className
        )}
        {...props}
      >
        {children}
        {hideArrow ? null : (
          <TooltipPrimitive.Arrow className="bg-foreground fill-foreground z-[10000] size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]" />
        )}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
